import { createSlice } from '@reduxjs/toolkit'
import { Auth } from 'aws-amplify'
import { API } from 'src/utils/interceptor'
import Sentry from '../utils/sentry'
import {
  RejectedNotification,
  SuccessNotification,
} from '../notifications/SuccessNotification'
import { invalidateReceiptingOrdersCache } from './ReceiptingSlice'
import { canShowRemoveFromPoAction } from '../utils/invoiceRemoveFromPoHelpers'
import { normalizeRematchError } from '../utils/rematchErrorHelpers'
import {
  OLLIE_FINDINGS_WRITE_EVENTS,
  ollieFindingsSaved,
} from 'src/utils/ollieFindingsRefresh'
import { notifyVenueHomeChanged } from 'src/utils/venueHomeApi'

const initialState = {
  uploadedInvoices: [],
  archivedInvoices: [],
  uploadedLoading: false,
  archivedLoading: false,
  updatingLoading: false,
}
const NewInvoicesSlice = createSlice({
  name: 'newInvoices',
  initialState,
  reducers: {
    setUploadedInvoices: (state, action) => {
      state.uploadedInvoices = action.payload
    },
    setUploadedLoading: (state, action) => {
      state.uploadedLoading = action.payload
    },
    setArchivedInvoices: (state, action) => {
      state.archivedInvoices = action.payload
    },
    setArchivedLoading: (state, action) => {
      state.archivedLoading = action.payload
    },
    setUpdatingLoading: (state, action) => {
      state.updatingLoading = action.payload
    },
    updateInvoices: (state, action) => {
      const { invoice } = action.payload
      if (!invoice?._id) {
        return
      }

      const invoiceId = String(invoice._id)
      state.uploadedInvoices = state.uploadedInvoices.map(uploadedInvoice => {
        if (String(uploadedInvoice._id) === invoiceId) {
          return { ...uploadedInvoice, ...invoice }
        }
        return uploadedInvoice
      })
    },
  },
})

export const {
  setUploadedInvoices,
  setArchivedInvoices,
  setArchivedLoading,
  setUploadedLoading,
  updateInvoices,
  setUpdatingLoading,
} = NewInvoicesSlice.actions

export const BLOCKED_ARCHIVE_STATUSES = [
  'attachedToPO',
  'reconciled',
  'sentToXero',
]

export const UNLINK_BLOCKED_STATUSES = ['sentToXero', 'reconciled']

/** Table UI page size — keep in sync with InvoicesScreen pagination. */
export const INVOICES_INFO_PAGE_SIZE = 10
/** Library fill (Receipting / Match) uses the same page size. */
const INVOICES_INFO_LIBRARY_LIMIT = INVOICES_INFO_PAGE_SIZE
const INVOICES_INFO_FETCH_CONCURRENCY = 5

function parseInvoicesInfoResponse(payload) {
  if (Array.isArray(payload)) {
    return {
      items: payload,
      hasMore: false,
      paged: false,
      total: payload.length,
      summary: null,
    }
  }
  if (payload && Array.isArray(payload.items)) {
    return {
      items: payload.items,
      hasMore: Boolean(payload.hasMore),
      paged: true,
      total: Number(payload.total) || payload.items.length,
      summary: payload.summary || null,
    }
  }
  return {
    items: [],
    hasMore: false,
    paged: false,
    total: 0,
    summary: null,
  }
}

function buildInvoicesInfoQuery({
  page = 1,
  limit = INVOICES_INFO_PAGE_SIZE,
  matchingStatuses = [],
  lifecycleStatuses = [],
  sources = [],
  uploadDateFrom = null,
  uploadDateTo = null,
  downloadStatus = 'all',
  search = '',
  includeSummary = false,
} = {}) {
  const queryStringParameters = {
    page,
    limit,
  }

  if (Array.isArray(matchingStatuses) && matchingStatuses.length) {
    queryStringParameters.matching = matchingStatuses.join(',')
  }
  if (Array.isArray(lifecycleStatuses) && lifecycleStatuses.length) {
    queryStringParameters.lifecycle = lifecycleStatuses.join(',')
  }
  if (Array.isArray(sources) && sources.length) {
    queryStringParameters.source = sources.join(',')
  }
  if (uploadDateFrom) {
    queryStringParameters.uploadDateFrom =
      typeof uploadDateFrom?.toISOString === 'function'
        ? uploadDateFrom.toISOString()
        : String(uploadDateFrom)
  }
  if (uploadDateTo) {
    queryStringParameters.uploadDateTo =
      typeof uploadDateTo?.toISOString === 'function'
        ? uploadDateTo.toISOString()
        : String(uploadDateTo)
  }
  if (downloadStatus && downloadStatus !== 'all') {
    queryStringParameters.downloadStatus = downloadStatus
  }
  if (String(search || '').trim()) {
    queryStringParameters.search = String(search).trim()
  }
  if (includeSummary) {
    queryStringParameters.includeSummary = '1'
  }

  return queryStringParameters
}

async function fetchInvoicesInfoPage(venueId, page, limit, filters = {}) {
  return API.get('api', `venue/${venueId}/invoicesInfo`, {
    queryStringParameters: buildInvoicesInfoQuery({
      page,
      limit,
      ...filters,
    }),
  })
}

/**
 * Invoices tab: one server page (+ optional filters). Does not replace the
 * full uploadedInvoices library used by Receipting / Match.
 */
export const getInvoicesTablePage =
  (venueId, options = {}) =>
  async dispatch => {
    const key = String(venueId || '').trim()
    if (!key) {
      return {
        items: [],
        page: 1,
        limit: INVOICES_INFO_PAGE_SIZE,
        total: 0,
        hasMore: false,
        summary: null,
      }
    }

    const page = Math.max(1, Number(options.page) || 1)
    const limit = Math.max(1, Number(options.limit) || INVOICES_INFO_PAGE_SIZE)
    const silent = Boolean(options.silent)

    try {
      if (!silent) {
        dispatch(setUploadedLoading(true))
      }

      const [payload, linkedResponse] = await Promise.all([
        fetchInvoicesInfoPage(key, page, limit, {
          matchingStatuses: options.matchingStatuses,
          lifecycleStatuses: options.lifecycleStatuses,
          sources: options.sources,
          uploadDateFrom: options.uploadDateFrom,
          uploadDateTo: options.uploadDateTo,
          downloadStatus: options.downloadStatus,
          search: options.search,
          includeSummary: Boolean(options.includeSummary),
        }),
        API.get('api2', `invoices/linked-ids/${key}`).catch(() => ({
          linkedInvoiceIds: [],
        })),
      ])

      const parsed = parseInvoicesInfoResponse(payload)
      const linkedSet = new Set(linkedResponse?.linkedInvoiceIds || [])
      const items = mapActiveInvoicesWithLinkedFlag(parsed.items, linkedSet)

      return {
        items,
        page: parsed.paged ? page : 1,
        limit,
        total: parsed.total,
        hasMore: parsed.hasMore,
        summary: parsed.summary,
      }
    } catch (e) {
      console.log(e)
      Sentry.captureException(e)
      return {
        items: [],
        page,
        limit,
        total: 0,
        hasMore: false,
        summary: null,
        error: e,
      }
    } finally {
      if (!silent) {
        dispatch(setUploadedLoading(false))
      }
    }
  }

async function mapPool(items, concurrency, mapper) {
  const results = new Array(items.length)
  let nextIndex = 0

  const workers = Array.from(
    { length: Math.min(concurrency, items.length) },
    async () => {
      while (nextIndex < items.length) {
        const current = nextIndex
        nextIndex += 1
        results[current] = await mapper(items[current], current)
      }
    },
  )

  await Promise.all(workers)
  return results
}

/**
 * Load the full invoice library as small DB-backed pages.
 * Used by Receipting / Match — not the Invoices table page fetch.
 */
async function fetchInvoicesInfoLibrary(venueId) {
  const first = parseInvoicesInfoResponse(
    await fetchInvoicesInfoPage(venueId, 1, INVOICES_INFO_LIBRARY_LIMIT),
  )
  if (!first.paged) {
    return first.items
  }

  const items = [...first.items]
  const total = first.total
  const pageCount = Math.max(1, Math.ceil(total / INVOICES_INFO_LIBRARY_LIMIT))

  if (pageCount <= 1) {
    return items
  }

  const remainingPages = []
  for (let page = 2; page <= pageCount; page += 1) {
    remainingPages.push(page)
  }

  const pageResults = await mapPool(
    remainingPages,
    INVOICES_INFO_FETCH_CONCURRENCY,
    async page =>
      parseInvoicesInfoResponse(
        await fetchInvoicesInfoPage(venueId, page, INVOICES_INFO_LIBRARY_LIMIT),
      ),
  )

  pageResults.forEach(result => {
    items.push(...(result.items || []))
  })

  return items
}

export const getArchiveBlockReason = invoice => {
  if (invoice?.status === 'sentToXero') {
    return 'This invoice has already been sent to Xero and cannot be archived.'
  }
  if (invoice?.status === 'reconciled') {
    return 'This invoice is reconciled and cannot be archived.'
  }
  if (invoice?.status === 'attachedToPO' || invoice?.isLinkedToPO) {
    return 'Remove from PO first (use "Not this order" in the Match modal), then archive.'
  }
  return null
}

export const canArchiveInvoice = invoice => !getArchiveBlockReason(invoice)

/** @deprecated Prefer canShowRemoveFromPoAction — soft-link + assigned PO. */
export const canUnlinkInvoice = invoice => canShowRemoveFromPoAction(invoice)

function mapActiveInvoicesWithLinkedFlag(invoices, linkedSet) {
  return (Array.isArray(invoices) ? invoices : [])
    .filter(invoice => !invoice.isArchived)
    .map(invoice => ({
      ...invoice,
      isLinkedToPO: linkedSet.has(invoice._id),
    }))
}

function invoiceNeedsStaleOpenRematch(invoice) {
  const status = invoice.matchingStatus
  const lifecycle = invoice.status || 'pending'
  if (
    lifecycle === 'reconciled' ||
    lifecycle === 'sentToXero' ||
    lifecycle === 'attachedToPO'
  ) {
    return false
  }
  return status === 'no_match' || status === 'needs_action'
}

/**
 * Best-effort stale rematch after the list has already painted.
 * Returns a refreshed active list, or null when nothing changed / rematch skipped.
 */
async function rematchStaleUploadedInvoices({
  venueId,
  activeInvoices,
  linkedSet,
}) {
  let nextInvoices = activeInvoices
  let didRefresh = false

  const hasPossibleDuplicates = nextInvoices.some(
    invoice => invoice.matchingStatus === 'possible_duplicate',
  )
  if (hasPossibleDuplicates) {
    try {
      const rematchResult = await API.post(
        'api2',
        `invoices/rematch-stale-duplicates/${venueId}`,
        { body: {} },
      )
      if (rematchResult?.rematchedInvoiceIds?.length) {
        const refreshed = await fetchInvoicesInfoLibrary(venueId)
        nextInvoices = mapActiveInvoicesWithLinkedFlag(refreshed, linkedSet)
        didRefresh = true
      }
    } catch (rematchError) {
      console.log('Stale duplicate rematch skipped', rematchError)
    }
  }

  // Invoice-first + later manual/OMS order: re-check capped no_match /
  // needs_action rows so the Match queue is not stale on refresh/nav.
  const hasStaleOpenMatches = nextInvoices.some(invoiceNeedsStaleOpenRematch)
  if (hasStaleOpenMatches) {
    try {
      const rematchResult = await API.post(
        'api2',
        `invoices/rematch-stale-open-matches/${venueId}`,
        { body: {} },
      )
      if (rematchResult?.rematchedInvoiceIds?.length) {
        const refreshed = await fetchInvoicesInfoLibrary(venueId)
        nextInvoices = mapActiveInvoicesWithLinkedFlag(refreshed, linkedSet)
        didRefresh = true
      }
    } catch (rematchError) {
      console.log('Stale open-match rematch skipped', rematchError)
    }
  }

  return didRefresh ? nextInvoices : null
}

/** One in-flight library load per venue — stops remount/rematch storms. */
const inflightUploadedInvoicesByVenue = new Map()

export const getUploadedInvoices =
  (venueId, options = {}) =>
  dispatch => {
    const key = String(venueId || '').trim()
    if (!key) return Promise.resolve(false)

    const existing = inflightUploadedInvoicesByVenue.get(key)
    if (existing) return existing

    // silent: refresh rows without swapping the screen for <Loading />
    // (e.g. Match open rematch — modal must stay mounted).
    const silent = Boolean(options?.silent)

    const loadPromise = (async () => {
      try {
        if (!silent) {
          dispatch(setUploadedLoading(true))
        }

        const [invoices, linkedResponse] = await Promise.all([
          fetchInvoicesInfoLibrary(key),
          API.get('api2', `invoices/linked-ids/${key}`).catch(() => ({
            linkedInvoiceIds: [],
          })),
        ])

        const linkedSet = new Set(linkedResponse?.linkedInvoiceIds || [])
        const activeInvoices = mapActiveInvoicesWithLinkedFlag(
          invoices,
          linkedSet,
        )

        // Paint the table immediately — rematch must not hold the spinner.
        dispatch(setUploadedInvoices(activeInvoices))
        if (!silent) {
          dispatch(setUploadedLoading(false))
        }

        const refreshed = await rematchStaleUploadedInvoices({
          venueId: key,
          activeInvoices,
          linkedSet,
        })
        if (refreshed) {
          dispatch(setUploadedInvoices(refreshed))
          return refreshed
        }

        return activeInvoices
      } catch (e) {
        console.log(e)
        Sentry.captureException(e)
        dispatch(setUploadedLoading(false))
        return false
      } finally {
        inflightUploadedInvoicesByVenue.delete(key)
      }
    })()

    inflightUploadedInvoicesByVenue.set(key, loadPromise)
    return loadPromise
  }

export const getInvoiceUrlById = async (venueId, invoiceId) => {
  try {
    const invoiceByIdWithUrl = await API.get(
      'api',
      `venue/${venueId}/invoice/${invoiceId}`,
    )

    return invoiceByIdWithUrl
  } catch (e) {
    console.log(e)
    Sentry.captureException(e)
  }
}
export const getInvoicesWithTempUrl = async (venueId, supplierId) => {
  try {
    const invoicesWithTempUrl = await API.get(
      'api',
      `venue/${venueId}/invoices_photos?supplierTitle=${supplierId}`,
    )

    return invoicesWithTempUrl
  } catch (e) {
    console.log(e)
    Sentry.captureException(e)
  }
}

export const searchPurchaseOrders = async (venueId, query = '', supplierId) => {
  const response = await API.get('api2', `orders/${venueId}/search-po`, {
    queryStringParameters: {
      query,
      limit: 20,
      ...(supplierId ? { supplierId } : {}),
    },
  })

  return response?.data || []
}

/**
 * Soft-link an invoice or credit note to a PO / order (no reconcile).
 * Body: { purchaseOrderNumber?, linkedOrderId?, orderIds?, useRecommended?, softLinkedInvoiceId? }
 * Credit notes return linked.creditApplied + remainingPennies (Chunk C2).
 */
export async function linkInvoiceToPurchaseOrder(invoiceId, body = {}) {
  if (!invoiceId) {
    throw new Error('Missing invoice id')
  }

  const response = await API.post('api2', `invoices/${invoiceId}/link`, {
    body,
  })

  if (response?.success === false) {
    throw new Error(response?.message || 'Could not link invoice to PO')
  }
  ollieFindingsSaved({
    event: OLLIE_FINDINGS_WRITE_EVENTS.INVOICE_SAVED,
  })

  return response
}

/**
 * Clear soft-link or assigned/suggested PO (Remove from this PO / Not this order).
 * Rematch runs on the server.
 */
export async function removeInvoiceFromPurchaseOrder(
  invoiceId,
  {
    venueId,
    username,
    supplierId = null,
    purchaseOrderNumber = null,
    orderIds = null,
  } = {},
) {
  if (!invoiceId) {
    throw new Error('Missing invoice id')
  }
  if (!venueId) {
    throw new Error('Missing venue id')
  }

  const response = await API.post('api2', `invoices/${invoiceId}/unlink`, {
    body: {
      venueId,
      ...(username ? { username } : {}),
      ...(supplierId ? { supplierId } : {}),
      ...(purchaseOrderNumber ? { purchaseOrderNumber } : {}),
      ...(Array.isArray(orderIds) && orderIds.length ? { orderIds } : {}),
    },
  })

  if (response?.success === false) {
    throw new Error(
      response?.message || 'Could not remove invoice from purchase order',
    )
  }
  ollieFindingsSaved({
    venueId,
    event: OLLIE_FINDINGS_WRITE_EVENTS.INVOICE_SAVED,
  })

  return response
}

/**
 * Save multi-PO allocations (one invoice split across several POs).
 * Body: { allocations: [{ purchaseOrderNumber, amountPennies, orderId? }] }
 * Express enforces sum(amountPennies) === invoice total.
 */
export async function saveInvoicePoAllocations(invoiceId, allocations = []) {
  if (!invoiceId) {
    throw new Error('Missing invoice id')
  }

  const response = await API.post('api2', `invoices/${invoiceId}/allocations`, {
    body: { allocations },
  })

  if (response?.success === false) {
    throw new Error(response?.message || 'Could not save PO allocations')
  }
  ollieFindingsSaved({
    event: OLLIE_FINDINGS_WRITE_EVENTS.INVOICE_SAVED,
  })

  return response
}

/**
 * One-click / force reconcile via express (revalidates then Full-accepts on server).
 * Pass force=true only after the outside-tolerance warning is confirmed.
 * Pass purchaseOrderNumber to close one allocated PO slice (multi-PO Step 8).
 *
 * Full accept is posted from the browser to venue/receipting/create (same Cognito
 * id-token path as Goods Receipting). Express then finalizes invoice status.
 * Express still retries create as a fallback when the browser step fails.
 */
export async function reconcileInvoiceOneClick(
  invoiceId,
  { force = false, acceptDifference = false, purchaseOrderNumber = '' } = {},
) {
  if (!invoiceId) {
    throw new Error('Missing invoice id')
  }

  const body = {}
  if (acceptDifference) {
    body.acceptDifference = true
    body.force = true
  } else if (force) {
    body.force = true
  }
  const po = String(purchaseOrderNumber || '').trim()
  if (po) body.purchaseOrderNumber = po

  const prep = await API.post(
    'api2',
    `invoices/${invoiceId}/reconcile/prepare`,
    { body },
  )

  if (prep?.success === false) {
    throw new Error(prep?.message || 'Could not prepare reconcile')
  }

  if (prep?.alreadyReconciled || prep?.alreadyReconciledAllocation) {
    notifyVenueHomeChanged()
    return prep
  }

  if (Array.isArray(prep?.payload) && prep.payload.length) {
    try {
      // Same path as Goods Receipting Full accept (id token via api gateway).
      await API.post('api', 'venue/receipting/create', {
        body: prep.payload,
      })
    } catch (createError) {
      console.error(
        'Match & reconcile: browser Full accept failed; express will retry',
        createError,
      )
      Sentry.captureException(createError)
    }
  }

  let serverAuthorization
  try {
    const session = await Auth.currentSession()
    serverAuthorization = session.getIdToken().getJwtToken()
  } catch (_) {
    serverAuthorization = undefined
  }

  const response = await API.post('api2', `invoices/${invoiceId}/reconcile`, {
    body,
    headers: serverAuthorization
      ? { 'X-Server-Authorization': serverAuthorization }
      : undefined,
  })

  if (response?.success === false) {
    throw new Error(response?.message || 'Could not reconcile invoice')
  }
  notifyVenueHomeChanged()
  ollieFindingsSaved({
    event: OLLIE_FINDINGS_WRITE_EVENTS.INVOICE_SAVED,
  })

  return response
}

/**
 * Accept an invoice-vs-receipt discrepancy and reconcile without requesting
 * credit. Preserves original expected/received/invoiced values.
 */
export async function acceptInvoiceDifference(invoiceId, options = {}) {
  return reconcileInvoiceOneClick(invoiceId, {
    ...options,
    force: true,
    acceptDifference: true,
  })
}

/**
 * Remove reconciliation from an invoice while keeping the uploaded file.
 * Optional purchaseOrderNumber unreconciles one multi-PO slice.
 */
export async function unreconcileInvoiceRequest(
  invoiceId,
  { purchaseOrderNumber = '' } = {},
) {
  if (!invoiceId) {
    throw new Error('Missing invoice id')
  }

  const body = {}
  const po = String(purchaseOrderNumber || '').trim()
  if (po) body.purchaseOrderNumber = po

  const response = await API.post('api2', `invoices/${invoiceId}/unreconcile`, {
    body,
  })

  if (response?.success === false) {
    throw new Error(response?.message || 'Could not unreconcile invoice')
  }
  ollieFindingsSaved({
    event: OLLIE_FINDINGS_WRITE_EVENTS.INVOICE_SAVED,
  })

  return response
}

/**
 * Re-run smart match for one invoice (Possible Duplicate "Check again").
 * Does not rematch the whole queue.
 */
export async function rematchInvoiceMatching(
  invoiceId,
  { manualPurchaseOrderNumber } = {},
) {
  if (!invoiceId) {
    throw new Error('Missing invoice id')
  }

  let response
  try {
    response = await API.post('api2', `invoices/${invoiceId}/rematch`, {
      body:
        manualPurchaseOrderNumber === undefined
          ? {}
          : { manualPurchaseOrderNumber },
    })
  } catch (apiError) {
    throw normalizeRematchError(apiError, 'Could not check matching again')
  }

  if (response?.success === false) {
    throw new Error(response?.message || 'Could not check matching again')
  }

  return response
}

/**
 * After Goods Receipting save: rematch open invoices for the saved PO(s)
 * so receipted totals refresh Matching without hunting Match.
 */
export async function rematchInvoicesForPurchaseOrders(
  venueId,
  purchaseOrderNumbers = [],
) {
  if (!venueId) {
    throw new Error('Missing venue id')
  }
  const pos = (
    Array.isArray(purchaseOrderNumbers)
      ? purchaseOrderNumbers
      : [purchaseOrderNumbers]
  )
    .map(po => String(po || '').trim())
    .filter(Boolean)

  if (!pos.length) {
    throw new Error('Missing purchase order number')
  }

  const response = await API.post(
    'api2',
    `invoices/rematch-for-po/${venueId}`,
    {
      body: { purchaseOrderNumbers: pos },
    },
  )

  if (response?.success === false) {
    throw new Error(
      response?.message || 'Could not rematch invoices after Goods Receipting',
    )
  }

  return response
}

export async function dismissInvoiceDuplicate(invoiceId) {
  if (!invoiceId) {
    throw new Error('Missing invoice id')
  }

  const response = await API.post(
    'api2',
    `invoices/${invoiceId}/dismiss-duplicate`,
    { body: {} },
  )

  if (response?.success === false) {
    throw new Error(response?.message || 'Could not mark as not a duplicate')
  }

  return response
}

/**
 * Confirm dual-photo Add-pages: merge stub file onto existing Invoice and
 * remove the stub row so Match/reconcile happens once.
 */
export async function mergeInvoicePages(invoiceId, body = {}) {
  if (!invoiceId) {
    throw new Error('Missing invoice id')
  }

  const response = await API.post('api2', `invoices/${invoiceId}/merge-pages`, {
    body: {
      ...(body?.targetInvoiceId
        ? { targetInvoiceId: body.targetInvoiceId }
        : {}),
    },
  })

  if (response?.success === false) {
    throw new Error(
      response?.message || 'Could not add pages to existing invoice',
    )
  }

  return response
}

export const getInvoicesWithTempUrlByRange = async (venueId, rangeString) => {
  try {
    console.log('rangeString', rangeString[0], rangeString[1])
    const invoicesWithTempUrl = await API.get(
      'api',
      `venue/${venueId}/invoices_photos?start=${rangeString[0]}&end=${rangeString[1]}`,
    )
    console.log('invoicesWithTempUrl', invoicesWithTempUrl)
    return invoicesWithTempUrl
  } catch (e) {
    console.log('Error in getInvoicesWithTempUrlByRange', e)
  }
}

export const getArchivedInvoices = venueId => async dispatch => {
  try {
    dispatch(setArchivedLoading(true))

    const response = await API.get('api2', `invoices/archived/${venueId}`)

    dispatch(setArchivedInvoices(response.data || []))
    dispatch(setArchivedLoading(false))
  } catch (e) {
    console.log('Error fetching archived invoices', e)
    Sentry.captureException(e)
    dispatch(setArchivedLoading(false))
    RejectedNotification({
      message: 'Something went wrong',
      description:
        e?.response?.data?.message || 'Failed to load archived invoices',
    })
  }
}

export const archiveInvoice =
  (invoiceId, venueId, archivedBy) => async dispatch => {
    try {
      const result = await API.post('api2', `invoices/${invoiceId}/archive`, {
        body: { venueId, archivedBy },
      })

      // Rematch of Possible Duplicate peers happens server-side; reload the
      // active list so survivors show the new matching status.
      await dispatch(getUploadedInvoices(venueId))

      SuccessNotification({
        message: result.message,
        description:
          Array.isArray(result.rematchedInvoiceIds) &&
          result.rematchedInvoiceIds.length
            ? 'Related Possible Duplicate invoices were checked again.'
            : undefined,
      })
    } catch (e) {
      console.log('Error in archiving invoice = ', invoiceId, e)
      RejectedNotification({
        message: 'Cannot archive invoice',
        description:
          e?.response?.data?.message || e?.message || 'Please, try again!',
      })
    }
  }

export const bulkArchiveInvoices =
  (venueId, invoiceIds, archivedBy) => async dispatch => {
    try {
      const result = await API.post('api2', 'invoices/bulk-archive', {
        body: {
          venueId,
          invoiceIds,
          archivedBy,
        },
      })

      await dispatch(getUploadedInvoices(venueId))

      const failures = (result?.results || []).filter(item => !item.success)

      if (result?.succeeded > 0) {
        SuccessNotification({
          message: result.message || `${result.succeeded} invoices archived.`,
          description:
            failures.length > 0
              ? `${failures.length} could not be archived.`
              : undefined,
        })
      } else {
        RejectedNotification({
          message: 'No invoices were archived',
          description:
            result?.message ||
            'Please review the selected invoices and try again.',
        })
      }

      return result
    } catch (e) {
      console.log('Error in bulk archiving invoices', e)
      Sentry.captureException(e)
      RejectedNotification({
        message: 'Cannot archive invoices',
        description:
          e?.response?.data?.message || e?.message || 'Please, try again!',
      })
      throw e
    }
  }

export const bulkDeleteInvoices = (venueId, invoiceIds) => async dispatch => {
  try {
    const result = await API.post('api2', 'invoices/bulk-delete', {
      body: {
        venueId,
        invoiceIds,
      },
    })

    await dispatch(getArchivedInvoices(venueId))

    const failures = (result?.results || []).filter(item => !item.success)

    if (result?.succeeded > 0) {
      SuccessNotification({
        message:
          result.message || `${result.succeeded} invoices permanently deleted.`,
        description:
          failures.length > 0
            ? `${failures.length} could not be deleted.`
            : undefined,
      })
    } else {
      RejectedNotification({
        message: 'No invoices were deleted',
        description:
          result?.message ||
          'Please review the selected invoices and try again.',
      })
    }

    return result
  } catch (e) {
    console.log('Error in bulk deleting invoices', e)
    Sentry.captureException(e)
    RejectedNotification({
      message: 'Cannot delete invoices',
      description:
        e?.response?.data?.message || e?.message || 'Please, try again!',
    })
    throw e
  }
}

export const restoreInvoice = (invoiceId, venueId) => async dispatch => {
  try {
    const result = await API.post('api2', `invoices/${invoiceId}/restore`, {
      body: { venueId },
    })

    dispatch(getArchivedInvoices(venueId))
    dispatch(getUploadedInvoices(venueId))

    SuccessNotification({ message: result.message })
  } catch (e) {
    console.log('Error in restoring invoice = ', invoiceId, e)
    RejectedNotification({
      message: 'Cannot restore invoice',
      description:
        e?.response?.data?.message || e?.message || 'Please, try again!',
    })
  }
}

export const permanentlyDeleteArchivedInvoice =
  (invoiceId, venueId) => async dispatch => {
    try {
      const result = await API.post(
        'api2',
        `invoices/${invoiceId}/permanent-delete`,
        { body: { venueId } },
      )

      dispatch(getArchivedInvoices(venueId))

      SuccessNotification({ message: result.message })
    } catch (e) {
      console.log('Error in permanently deleting invoice = ', invoiceId, e)
      RejectedNotification({
        message: 'Cannot permanently delete invoice',
        description:
          e?.response?.data?.message || e?.message || 'Please, try again!',
      })
    }
  }

/**
 * Record that the current user downloaded an invoice file.
 * Non-blocking for the browser download itself — callers should still
 * complete the file download even if this fails.
 */
export async function recordInvoiceDownload(
  invoiceId,
  { venueId, downloadedBy, downloadedByName } = {},
) {
  if (!invoiceId) {
    throw new Error('Missing invoice id')
  }

  const response = await API.post(
    'api2',
    `invoices/${invoiceId}/record-download`,
    {
      body: {
        venueId,
        ...(downloadedBy ? { downloadedBy } : {}),
        ...(downloadedByName ? { downloadedByName } : {}),
      },
    },
  )

  if (response?.success === false) {
    throw new Error(response?.message || 'Could not record invoice download')
  }

  return response
}

export const unlinkInvoiceFromPO =
  (invoiceId, venueId, username) => async dispatch => {
    try {
      const result = await removeInvoiceFromPurchaseOrder(invoiceId, {
        venueId,
        username,
      })

      dispatch(invalidateReceiptingOrdersCache())
      dispatch(getUploadedInvoices(venueId))

      SuccessNotification({ message: result.message })
    } catch (e) {
      console.log('Error removing invoice from PO = ', invoiceId, e)
      RejectedNotification({
        message: 'Cannot remove invoice from PO',
        description:
          e?.response?.data?.message || e?.message || 'Please, try again!',
      })
    }
  }

export const unreconcileInvoiceFromPO =
  (invoiceId, { purchaseOrderNumber = '' } = {}) =>
  async dispatch => {
    try {
      dispatch(setUpdatingLoading(true))
      const result = await unreconcileInvoiceRequest(invoiceId, {
        purchaseOrderNumber,
      })

      dispatch(setUpdatingLoading(false))

      const warningMessages = (result?.warnings || [])
        .map(warning => warning?.message)
        .filter(Boolean)

      SuccessNotification({
        message: result?.message || 'Invoice unreconciled',
        description: warningMessages.length
          ? warningMessages.join(' ')
          : 'It remains linked to the purchase order. You can reconcile again when ready.',
      })
      setTimeout(() => {
        window.location.reload()
      }, 1000)
      // Force reload to refresh the invoice list and matching status
    } catch (e) {
      console.log('Error unreconciling invoice = ', invoiceId, e)
      dispatch(setUpdatingLoading(false))
      RejectedNotification({
        message: 'Cannot unreconcile invoice',
        description:
          e?.response?.data?.message || e?.message || 'Please, try again!',
      })
      return null
    }
  }

export const deleteInvoice = archiveInvoice

/**
 * Persist confirmed dashboard upload fields via express enrich.
 * Stamps source=customer_dashboard and OCR accept/correct feedback
 * when those are included in the body (see buildEnrichPayloadFromUploadFile).
 */
export async function enrichUploadedInvoice(invoiceId, body) {
  if (!invoiceId) {
    throw new Error('Missing invoice id for enrichment')
  }

  const response = await API.patch('api2', `invoices/${invoiceId}/enrich`, {
    body,
  })

  if (response?.success === false) {
    throw new Error(response?.message || 'Invoice enrichment failed')
  }

  return response
}

/** Enrich many confirmed upload rows (invoiceId + enrich body each). */
export async function enrichUploadedInvoices(items = []) {
  return Promise.all(
    items.map(item => enrichUploadedInvoice(item.invoiceId, item.body)),
  )
}

export async function discardUnconfirmedUploadedInvoice(invoiceId, venueId) {
  if (!invoiceId) {
    return null
  }

  const response = await API.post(
    'api2',
    `invoices/${invoiceId}/discard-upload`,
    { body: { venueId } },
  )

  if (response?.success === false) {
    throw new Error(response?.message || 'Could not discard invoice upload')
  }

  return response
}

export async function discardUnconfirmedUploadedInvoices(files = [], venueId) {
  const invoiceIds = [
    ...new Set(
      files
        .map(file => file?.invoiceId)
        .filter(invoiceId => invoiceId != null && invoiceId !== ''),
    ),
  ]

  return Promise.all(
    invoiceIds.map(invoiceId =>
      discardUnconfirmedUploadedInvoice(invoiceId, venueId),
    ),
  )
}

export const editInvoiceInDatabase =
  (venueId, invoiceId, data) => async (dispatch, getState) => {
    try {
      dispatch(setUpdatingLoading(true))
      const authAccount = getState()?.auth?.account || {}
      const actorName =
        authAccount.name ||
        authAccount.emailAddress ||
        authAccount.username ||
        ''
      const body = {
        ...data,
        actorName,
        username: authAccount.username || authAccount.emailAddress || '',
        actorType: authAccount.isAdmin ? 'oms' : 'customer',
      }

      const result = await API.patch(
        'api',
        `venue/${venueId}/invoiceUpdate/${invoiceId}`,
        {
          body,
        },
      )
      ollieFindingsSaved({
        venueId,
        event: OLLIE_FINDINGS_WRITE_EVENTS.INVOICE_SAVED,
        viaServer: true,
      })

      let nextInvoice = result?.invoice || null
      let rematchRefreshed = false

      // Rematch so Matching reflects corrected total / number / supplier / PO.
      if (nextInvoice?._id || invoiceId) {
        try {
          const rematch = await rematchInvoiceMatching(
            String(nextInvoice?._id || invoiceId),
            data?.purchaseOrderNumber !== undefined
              ? { manualPurchaseOrderNumber: data.purchaseOrderNumber }
              : {},
          )
          if (rematch?.data) {
            nextInvoice = rematch.data
            rematchRefreshed = true
          } else if (rematch?.matchingStatus && nextInvoice) {
            nextInvoice = {
              ...nextInvoice,
              matchingStatus: rematch.matchingStatus,
              matchingFailureReason:
                rematch.matchingFailureReason ??
                nextInvoice.matchingFailureReason,
              matchSummary: rematch.matchSummary ?? nextInvoice.matchSummary,
            }
            rematchRefreshed = true
          }
        } catch (rematchError) {
          console.error('Post-edit rematch failed', rematchError)
        }
      }

      const { newInvoices } = getState()
      const targetId = String(nextInvoice?._id || invoiceId)

      const updatedInvoices = (newInvoices.uploadedInvoices || []).map(
        invoice => {
          if (String(invoice._id) !== targetId) return invoice
          return nextInvoice || invoice
        },
      )

      dispatch(setUploadedInvoices(updatedInvoices))
      dispatch(setUpdatingLoading(false))
      dispatch(invalidateReceiptingOrdersCache())

      SuccessNotification({
        message: result?.message || 'Invoice updated',
        description: rematchRefreshed
          ? 'Matching has been refreshed with your changes.'
          : 'Invoice details saved. Matching may still need a refresh — open Match again if the status looks stale.',
      })

      return { invoice: nextInvoice, changes: result?.changes || null }
    } catch (e) {
      console.log('Error in updating invoice = ', invoiceId, e)
      dispatch(setUpdatingLoading(false))
      RejectedNotification({
        message: 'Something went wrong',
        description:
          e?.response?.data?.message || e?.message || 'Please, try again!',
      })
      return null
    }
  }
export const updateInvoice = (invoiceId, field, value) => async dispatch => {
  try {
    const body = { field, value }
    const updatedInvoice = await API.put(
      'api',
      `venue/${invoiceId}/makeChecked`,
      { body },
    )
    dispatch(updateInvoices(updatedInvoice))
  } catch (e) {
    console.log(e)
    Sentry.captureException(e)
  }
}
export default NewInvoicesSlice
